// `/connect`, per the 2026-09-06 UX execution plan §3/§8 (WS-3). The
// first-run landing IS `/accounts` in its `no-accounts` state (not a route of
// its own); this page is the "Connect a bank"/"Add manual account" flow
// reached from there, plus the connection-lifecycle list once at least one
// bank is connected. `?stage=` is a query param so a reload resumes; no
// institution picker is rebuilt here -- Plaid Link owns that, and this build
// does not launch it yet (see `launchLink.ts`).
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Skeleton } from '@moneyskyline/ui';
import { HonestInline } from '@/components/HonestInline';
import type { Account } from '@/api/hooks/useAccounts';
import { useItems } from '@/api/hooks/useItems';
import { useRemoveItem } from '@/api/hooks/useItemMutations';
import { ConnectStage, type ConnectStageName } from './ConnectStage';
import { ConnectionStateCard } from './ConnectionStateCard';
import { RemoveConnectionDialog } from './RemoveConnectionDialog';
import { ManualAccountForm } from './ManualAccountForm';

const VALID_STAGES: ConnectStageName[] = ['link', 'exchanging', 'first-sync', 'success', 'error'];

export function ConnectPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const itemsQuery = useItems();
  const removeItemMutation = useRemoveItem();

  const [exchangedAccounts, setExchangedAccounts] = useState<Account[] | null>(null);
  const [removeTarget, setRemoveTarget] = useState<{ itemId: string; institutionName: string | null } | null>(null);
  const [manualFormOpen, setManualFormOpen] = useState(() => searchParams.get('manual') === '1');
  const manualOpenerRef = useRef<HTMLButtonElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const stageHostRef = useRef<HTMLDivElement>(null);
  const removeOpenerRef = useRef<HTMLElement | null>(null);
  const manualOpenedFromTrigger = useRef(false);
  const wasManualFormOpen = useRef(manualFormOpen);
  const wasRemoveDialogOpen = useRef(false);

  const stageParam = searchParams.get('stage');
  const stage = VALID_STAGES.includes(stageParam as ConnectStageName) ? (stageParam as ConnectStageName) : null;
  const itemId = searchParams.get('item');
  const jobId = searchParams.get('job');
  const previousStageRef = useRef(stage);

  const goToAccounts = useCallback(() => navigate('/accounts'), [navigate]);

  const startLink = useCallback(
    (targetItemId?: string | null) => {
      const params: Record<string, string> = { stage: 'link' };
      if (targetItemId) params.item = targetItemId;
      setSearchParams(params);
    },
    [setSearchParams],
  );

  const clearStage = useCallback(() => setSearchParams({}), [setSearchParams]);

  const closeManualForm = useCallback((open: boolean) => {
    setManualFormOpen(open);
    if (!open && searchParams.has('manual')) {
      const next = new URLSearchParams(searchParams);
      next.delete('manual');
      setSearchParams(next);
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (wasManualFormOpen.current && !manualFormOpen) {
      const fallback = manualOpenedFromTrigger.current ? manualOpenerRef.current : headingRef.current;
      manualOpenedFromTrigger.current = false;
      fallback?.focus();
    }
    wasManualFormOpen.current = manualFormOpen;
  }, [manualFormOpen]);

  useEffect(() => {
    if (wasRemoveDialogOpen.current && !removeTarget) {
      const opener = removeOpenerRef.current;
      removeOpenerRef.current = null;
      if (opener?.isConnected) {
        opener.focus();
      } else {
        headingRef.current?.focus();
      }
    }
    wasRemoveDialogOpen.current = removeTarget !== null;
  }, [removeTarget]);

  useEffect(() => {
    if (previousStageRef.current === stage) return;
    previousStageRef.current = stage;
    if (!manualFormOpen && !removeTarget) {
      headingRef.current?.focus({ preventScroll: true });
    }
  }, [manualFormOpen, removeTarget, stage]);

  useLayoutEffect(() => {
    const host = stageHostRef.current;
    if (!host) return undefined;

    const hideNestedRouteHeadings = () => {
      host.querySelectorAll('h1').forEach((heading) => heading.setAttribute('aria-hidden', 'true'));
    };
    hideNestedRouteHeadings();
    const observer = new MutationObserver(hideNestedRouteHeadings);
    observer.observe(host, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [stage]);

  const openManualForm = useCallback(() => {
    manualOpenedFromTrigger.current = true;
    setManualFormOpen(true);
  }, []);

  return (
    <section
      aria-labelledby="connect-page-heading"
      data-testid={stage ? 'connect-page' : 'connect-landing'}
      className={stage ? 'mx-auto flex max-w-lg flex-col gap-4 px-6 py-16' : 'mx-auto flex max-w-2xl flex-col gap-6 px-6 py-16'}
    >
      <h1 ref={headingRef} id="connect-page-heading" tabIndex={-1} className="text-title">
        Connect
      </h1>

      {stage ? (
        <div ref={stageHostRef} className="flex flex-col gap-4">
        <ConnectStage
          stage={stage}
          itemId={itemId}
          jobId={jobId}
          exchangedAccounts={exchangedAccounts}
          onExchanged={({ itemId: newItemId, jobId: newJobId, accounts }) => {
            setExchangedAccounts(accounts);
            setSearchParams({ stage: 'first-sync', item: newItemId, job: newJobId });
          }}
          onSyncDone={(doneJobId) => setSearchParams({ stage: 'success', item: itemId ?? '', job: doneJobId })}
          onContinue={goToAccounts}
          onExit={clearStage}
        />
        <ManualAccountForm open={manualFormOpen} onOpenChange={closeManualForm} />
        </div>
      ) : (
        <>
      {itemsQuery.isLoading && (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      )}

      {itemsQuery.isError && <HonestInline kind="api-unavailable" />}

      {itemsQuery.data && itemsQuery.data.length === 0 && (
        <div className="flex flex-col items-center gap-4 text-center">
          <h2 className="text-2xl font-bold">
            No accounts connected yet
          </h2>
          <p className="text-muted-foreground">
            Connect a bank to see your net worth here. No demonstration data has been substituted.
          </p>
          <button
            type="button"
            onClick={() => startLink()}
            className="min-h-11 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground"
          >
            Connect a bank
          </button>
          <button
            ref={manualOpenerRef}
            type="button"
            aria-label="Add a manual account instead"
            onClick={openManualForm}
            className="min-h-11 text-sm underline"
          >
            Add a manual account instead
          </button>
        </div>
      )}

      {itemsQuery.data && itemsQuery.data.length > 0 && (
        <div className="flex flex-col gap-4">
          <h2 className="text-title">Connections</h2>
          <div className="flex flex-col gap-3">
            {itemsQuery.data.map((item) => (
              <ConnectionStateCard
                key={item.item_id}
                item={item}
                reconnecting={false}
                onReconnect={(id) => startLink(id)}
                onRemove={(id) => {
                  removeOpenerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
                  setRemoveTarget({ itemId: id, institutionName: item.institution_name });
                }}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => startLink()}
              className="min-h-11 rounded-md border border-border px-4 py-2 text-sm"
            >
              Connect another bank
            </button>
            <button
              ref={manualOpenerRef}
              type="button"
              aria-label="Add a manual account"
              onClick={openManualForm}
              className="min-h-11 text-sm underline"
            >
              Add a manual account
            </button>
          </div>
        </div>
      )}

      {removeItemMutation.isError && (
        <div role="alert" className="rounded-lg border border-negative-border bg-negative-surface p-4 text-sm text-negative-foreground">
          We couldn&apos;t confirm removal. Check the connection status before trying again.
        </div>
      )}

      <RemoveConnectionDialog
        open={removeTarget !== null}
        institutionName={removeTarget?.institutionName ?? null}
        removing={removeItemMutation.isPending}
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(null);
        }}
        onConfirm={() => {
          if (!removeTarget) return;
          removeItemMutation.mutate(removeTarget.itemId, {
            onSuccess: () => setRemoveTarget(null),
          });
        }}
      />

      <ManualAccountForm open={manualFormOpen} onOpenChange={closeManualForm} />
        </>
      )}
    </section>
  );
}

export default ConnectPage;
